import { Countries } from '@constants/enums';
import { Button, Dialog, DialogContent } from '@efexteam/components';
import useUserPreferences from '@main-hooks/useUserPreferences';
import { UserPreferencesKeys } from '@main-hooks/useUserPreferences/useUserPreferences.types';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { IconButton, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useWhiteLabelService from '@/layout/login/hooks/useWhiteLabel.service';
import { tourStart } from '../../../../assets';
import { selectProfileData } from '../../../profile/store/selectors';
import usePaymentLinkTour from '../PaymentLinkButton/hooks/usePaymentLinkTour';
import useAddWalletTour from '../tabsWallets/hooks/useAddWalletTour';
import useMainTour from './useMainTour';

const Tour = () => {
  const { isWhiteLabel } = useWhiteLabelService();

  const profile = useSelector(selectProfileData);
  const { t } = useTranslation();
  const { preferences } = useUserPreferences();

  const { open, startTour, relaunchTutorial, setOpen, onTourFinish } =
    useMainTour();

  const { startTour: startAddWalletTour } = useAddWalletTour();
  const { startTour: startPaymentLinkTour } = usePaymentLinkTour();

  const countryOrigin = useMemo(
    () => profile?.company.country,
    [profile?.company.country]
  );

  const location = useLocation();

  const preferencesToursDictionary = useMemo(() => {
    const toursPreferencesKeys: string[] = Object.values(UserPreferencesKeys);

    const dictionary: Record<string, boolean> = {
      [UserPreferencesKeys.TUTORIAL_COMPLETED]: false,
      [UserPreferencesKeys.TUTORIAL_PAYMENT_LINK]: false,
      [UserPreferencesKeys.TUTORIAL_ADD_WALLET]: false,
    };

    preferences.forEach((preference) => {
      if (!toursPreferencesKeys.includes(preference.key)) return;

      dictionary[preference.key] = preference.value as boolean;
    });

    return dictionary;
  }, [preferences]);

  useEffect(() => {
    if (!profile) return;

    if (!preferencesToursDictionary[UserPreferencesKeys.TUTORIAL_COMPLETED]) {
      setOpen(true);
      return;
    }

    if (
      !preferencesToursDictionary[UserPreferencesKeys.TUTORIAL_ADD_WALLET] &&
      profile.isAwActive
    ) {
      startAddWalletTour();
      return;
    }

    if (
      !preferencesToursDictionary[UserPreferencesKeys.TUTORIAL_PAYMENT_LINK] &&
      profile.company.isActivePaymentLink
    ) {
      startPaymentLinkTour();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  return (
    <>
      {location.pathname === '/dashboard' &&
        preferencesToursDictionary[UserPreferencesKeys.TUTORIAL_COMPLETED] && (
          <IconButton
            onClick={relaunchTutorial}
            className="!bg-[#1A237E] w-[35px] h-[35px] p-0 z-[500] !fixed !bottom-[1rem] !right-[1rem] !text-white"
          >
            <MapOutlinedIcon sx={{ fontSize: '1.3rem' }} />
          </IconButton>
        )}

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          onTourFinish();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent className="text-center">
          <img
            src={tourStart}
            alt="Onboarding welcome"
            className="max-w-[350px] w-full my-4 mx-auto"
          />
          <Typography variant="h3">
            {t('tutorialGreeting')} {profile?.legalRepresentative.name}
          </Typography>
          <Typography variant="body1" className="!my-6">
            {t(isWhiteLabel ? 'lngWelcomeWhiteLabel' : 'lgnWelcomeEfex')}
            {countryOrigin === Countries.USA
              ? t('tutorialWelcomeMessageUS')
              : t('tutorialWelcomeMessage')}
          </Typography>
          <Button appearance="primary" autoFocus onClick={startTour}>
            {t('tutorialBegin')}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Tour;
